import { Fragment } from "react";
import { Button, CircularProgress, Divider } from "@mui/material";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "use-intl";
import type {
  FlightOffer,
  HotelBookingRoomOption,
} from "@/app/lib/types/types";
import { BookedHotelCard } from "../hotels-list/BookedHotelCard";
import { ReservationNextSteps } from "../my-bookings/ReservationNextSteps";
import { ReservationDocumentList } from "../ReservationDocumentList";
import { HotelRoom } from "./HotelRoom";
import { useOrder } from "@/app/lib/hooks/useOrder";
import { useHotelSearchDataContext } from "@/app/lib/context/HotelSearchDataContext";
import { reservationQueryOptions } from "@/app/lib/hooks/useReservations";

export const HotelBookingSuccess = ({
  orderId,
  rooms,
}: {
  flight?: FlightOffer | null;
  orderId: string;
  rooms: HotelBookingRoomOption[];
}) => {
  const navigate = useNavigate();

  const t = useTranslations();
  const { searchData } = useHotelSearchDataContext();
  const { data: orderData, isLoading: isOrderLoading } = useOrder(orderId);
  const reservationId = orderData?.reservations?.find(
    (reservation) => reservation.service === "hotel",
  )?.id;
  const { data: reservationData, isLoading: isReservationLoading } = useQuery(
    reservationQueryOptions(reservationId),
  );
  const reservation = reservationData?.hotelReservation;

  const isLoading =
    isOrderLoading ||
    !orderData?.reservations.length ||
    isReservationLoading ||
    reservationData?.hotelReservation.status === "IN_PROGRESS";

  return (
    <div className="px-4 pb-4">
      <div className="mb-2 p-2 text-center text-xl font-semibold text-text-primary">
        {t("hotels.overview.booked")}
      </div>

      <div className="section-title">{t("hotels.overview.hotelDetails")}</div>

      {isLoading ? (
        <section className="section-card text-center">
          <CircularProgress size={20} />
        </section>
      ) : (
        <BookedHotelCard
          reservation={reservation!.externalReservation}
          footer={
            <div className="flex flex-col gap-4 text-text-primary">
              {rooms.map((room, i) => (
                <Fragment key={room.roomToken}>
                  <Divider
                    sx={{
                      borderColor: "var(--color-stroke-neutral-softer)",
                    }}
                  />

                  <HotelRoom
                    room={room}
                    adultCount={searchData!.rooms[i].adults}
                    childrenCount={searchData!.rooms[i].children}
                  />
                </Fragment>
              ))}
            </div>
          }
        />
      )}

      <section className="section-card mt-2 mb-4 flex w-full items-center justify-between gap-4">
        <span className="section-title mb-0">
          {t("reservations.bookingNumber")}
        </span>
        <span className="text-base font-semibold text-text-title">
          {isLoading ? (
            <CircularProgress size={20} />
          ) : (
            reservation?.externalReservation.reference.external
          )}
        </span>
      </section>

      {orderData?.reservations.map((reservation) => (
        <ReservationDocumentList
          key={reservation.id}
          reservationId={reservation.reservationId}
        />
      ))}

      <ReservationNextSteps type="hotel" />

      <div className="mt-4 flex flex-col items-center justify-center gap-2">
        <Button
          sx={{
            width: "240px",
            padding: "8px",
          }}
          variant="contained"
          size="small"
          onClick={() => {
            navigate({ to: "/", search: { category: "hotels" } });
          }}
        >
          <span>{t("backToHome")}</span>
        </Button>
        <Button
          variant="text"
          size="small"
          sx={{
            width: "240px",
          }}
          onClick={() => {
            navigate({ to: "/bookings", search: { category: "hotels" } });
          }}
        >
          <span>{t("openMyBookings")}</span>
        </Button>
      </div>
    </div>
  );
};
